import Vue from 'vue'
import VueGtm from 'vue-gtm'
import { Store } from 'vuex'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { isEnabled, getProduct } from '../helpers'
import { buildUserData, resolveOrderAddress, setUserProvidedData } from '../helpers/enhancedConversions'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'

const isEnhancedConversionsEnabled = (gtmConfig: { enhanced_conversions?: boolean }): boolean => {
  return gtmConfig.enhanced_conversions !== false
}

export function afterRegistration (config, store: Store<any>) {
  if (isEnabled(config.googleTagManager.id)) {
    const GTM: VueGtm = (Vue as any).gtm

    const currencyCode = config.googleTagManager.default_currency
    const enhancedConversionsEnabled = isEnhancedConversionsEnabled(config.googleTagManager)

    store.subscribe(({ type, payload }, state) => {
      if (payload && payload.product) {
        // Adding a Product to a Shopping Cart
        if (type === 'cart/cart/ADD') {
          GTM.trackEvent({
            event: 'add_to_cart',
            ecommerce: {
              items: [
                getProduct(payload.product)
              ]
            }
          });
        }

        // Removing a Product from a Shopping Cart
        if (type === 'cart/cart/DEL') {
          GTM.trackEvent({
            event: 'remove_from_cart',
            ecommerce: {
              items: [
                getProduct(payload.product)
              ]
            }
          });
        }

        // Measuring Views of Product Details
        if (type === 'product/product/SET_CURRENT') {
          GTM.trackEvent({
            event: 'view_item',
            ecommerce: {
              items: [
                getProduct(payload.product)
              ]
            }
          });
        }

        // Measuring Views of Product Details
        if (type === 'product/product/SET_PRODUCT_CURRENT') {
          GTM.trackEvent({
            ecommerce: {
              items: [
                getProduct(payload.product)
              ]
            }
          });
        }
      } else {
        // Measuring Views of Product Details
        if (type === 'product/product/SET_CURRENT' && state.product && state.product.current) {
          GTM.trackEvent({
            event: 'view_item',
            ecommerce: {
              items: [
                getProduct(state.product.current)
              ]
            }
          });
        }
      }
    })

    EventBus.$on('order-success', async (event) => {
      const orderId = event.confirmation.backendOrderId

      const purchaseTrackedKey = `gtm_purchase_tracked_${orderId}`
      if (typeof localStorage !== 'undefined') {
        if (localStorage.getItem(purchaseTrackedKey)) {
          return
        }
        localStorage.setItem(purchaseTrackedKey, 'true')
      }

      const products = event.order.products.map(product => getProduct(product))

      let userData = null
      if (enhancedConversionsEnabled) {
        const address = resolveOrderAddress(event.order)
        userData = buildUserData(address)

        if (userData) {
          setUserProvidedData(userData)
        }
      }

      store.dispatch(
        'user/getOrdersHistory',
        { refresh: true, useCache: false }
      ).then(() => {
        const orderHistory = (store.state as any).user && (store.state as any).user.orders_history

        let order = null as any
        if (orderHistory && orderHistory.items) {
          order = orderHistory.items.find((o: any) => o['entity_id'] && o['entity_id'].toString() === orderId)
        }

        const totals = (event as any).totals || []
        const getTotalByCode = (code: string) => {
          const row = totals.find((t: any) => t && t.code === code)
          return row && row.value != null ? Number(row.value) : null
        }

        const grandTotalFromEvent = getTotalByCode('grand_total')
        const taxFromEvent = getTotalByCode('tax')
        const shippingFromEvent = getTotalByCode('shipping')

        const value = order ? order.total_due : (grandTotalFromEvent != null ? grandTotalFromEvent : '')
        const tax = order ? order.tax_amount : (taxFromEvent != null ? taxFromEvent : '')
        const shipping = order ? order.shipping_amount : (shippingFromEvent != null ? shippingFromEvent : '')
        const affiliation = order ? order.store_name : ''

        GTM.trackEvent({
          event: 'purchase',
          ...(userData ? { user_data: userData } : {}),
          ecommerce: {
            transaction_id: orderId,
            affiliation,
            value,
            tax,
            shipping,
            currency: currencyCode,
            coupon: '',
            items: products
          }
        })
      })
    })
  }
}
